import React, { useEffect, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { graphql, usePreloadedQuery, useQueryLoader, type PreloadedQuery } from 'react-relay';
import { PlaceholderUFOMigration as Placeholder } from '@atlassian/jira-placeholder/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import Query, {
	type JiraIssueSearchInput,
	type ChildRowsPreloadedQuery as ChildRowsPreloadedQueryType,
} from '@atlassian/jira-relay/src/__generated__/ChildRowsPreloadedQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	getChildQueryVariablesForJql,
	getChildQueryVariablesForParentIssue,
	type Column,
} from '../../common/utils/get-child-query-variables/index.tsx';
import LoadingIssueRow, { type RenderTableCell } from '../loading-row/index.tsx';
import {
	ChildRowsPagination,
	type BaseProps as ChildRowsPaginationBaseProps,
} from '../child-rows-pagination/ChildRowsPagination.tsx';
import type { CellBorderStyle } from '../../common/types.tsx';

interface BaseProps<TColumn extends Column, TSortOrder>
	extends ChildRowsPaginationBaseProps<TColumn> {
	columns: TColumn[];
	isFixedTableLayoutEnabled: boolean;
	isReparentingEnabled: boolean;
	isTimelineEnabled: boolean;
	actionColumns: string[];
	amountOfColumns: number;
	sortDirection: TSortOrder;
	sortField?: string;
	jql?: string;
	isHideDoneItemsEnabled: boolean;
	renderTableCell: RenderTableCell;
	isConditionalFormattingEnabled: boolean;
	isInlineFieldConfigEnabled: boolean;
	isStickyColumnsVisible?: boolean;
	isLinkRelationshipFieldEnabled: boolean;
	cellBorderStyle?: CellBorderStyle;
}

export type RowLoaderProps = {
	type: 'PARENT_CHILDREN';
	issueKey: string;
	groupJql?: never;
};

export type GroupLoaderProps = {
	type: 'GROUP_CHILDREN';
	groupJql: string;
	issueKey?: never;
};

export type Props<TColumn extends Column, TSortOrder> = BaseProps<TColumn, TSortOrder> &
	(RowLoaderProps | GroupLoaderProps);

export const ChildRowsLoader = <TColumn extends Column, TSortOrder>(
	props: Props<TColumn, TSortOrder>,
) => {
	const [issueSearch, loadQuery, dispose] = useQueryLoader<ChildRowsPreloadedQueryType>(Query);

	const cloudId = useCloudId();

	const {
		sortField,
		sortDirection,
		jql,
		columns,
		isReparentingEnabled,
		isTimelineEnabled,
		isGroupingSupported,
		pageSize,
		amountOfColumns,
		actionColumns,
		isHideDoneItemsEnabled,
		projectKey,
		isConditionalFormattingEnabled,
		isInlineFieldConfigEnabled,
		isStickyColumnsVisible,
		isLinkRelationshipFieldEnabled,
	} = props;

	const variables = useMemo(
		() =>
			props.type === 'GROUP_CHILDREN'
				? getChildQueryVariablesForJql<TColumn>({
						cloudId,
						columns,
						jql: props.groupJql,
						isReparentingEnabled: false,
						isTimelineEnabled,
						actionColumns,
						amountOfColumns,
						pageSize,
						isHideDoneItemsEnabled,
						isConditionalFormattingEnabled,
						projectKey,
						isInlineFieldConfigEnabled,
						isLinkRelationshipFieldEnabled,
					})
				: getChildQueryVariablesForParentIssue<TColumn, TSortOrder>({
						cloudId,
						columns,
						parentIssueKey: props.issueKey,
						sortDirection,
						sortField,
						jql,
						isReparentingEnabled,
						isTimelineEnabled,
						actionColumns,
						amountOfColumns,
						pageSize,
						isHideDoneItemsEnabled,
						isConditionalFormattingEnabled,
						isInlineFieldConfigEnabled,
						isLinkRelationshipFieldEnabled,
					}),
		[
			props.type,
			props.groupJql,
			props.issueKey,
			cloudId,
			columns,
			actionColumns,
			amountOfColumns,
			pageSize,
			sortDirection,
			sortField,
			jql,
			isConditionalFormattingEnabled,
			isHideDoneItemsEnabled,
			isReparentingEnabled,
			isTimelineEnabled,
			projectKey,
			isInlineFieldConfigEnabled,
			isLinkRelationshipFieldEnabled,
		],
	);
	const prevIssueSearchInput = useRef<JiraIssueSearchInput | null>(variables.issueSearchInput);

	useEffect(() => {
		// This check prevents performing another network request for child items when the issue query changes
		// (which is unused as we re-fetch the issues when this happens). This is due to a race condition where
		// this component has not yet been unmounted when the issue query changes.
		if (
			props.isGroupingSupported &&
			!isEqual(variables.issueSearchInput, prevIssueSearchInput.current)
		) {
			return;
		}

		loadQuery(variables);

		if (props.isGroupingSupported) {
			prevIssueSearchInput.current = variables.issueSearchInput;
		}
		return () => {
			dispose();
		};
	}, [variables, loadQuery, dispose, props.isGroupingSupported]);

	if (!issueSearch) {
		return null;
	}

	const {
		groupId,
		depth,
		parentItemId,
		itemsConnectionId,
		parentIssueAri,
		parentItemConnectionId,
		parentIssueRelayConnectionId,
		parentIssueHierarchyLevel,
		connectionMetadata,
		isFormVisible,
		formProps,
		rankFieldId,
		childItemsExperience,
		childItemsLoadMoreExperience,
		analyticsSource,
		width,
		isIssueRankDescending,
		renderRows,
		renderCreateIssueController,
		renderDraggableWrapper,
		ChildIssueCreateRowComponent,
	} = props;

	return (
		<Placeholder
			name="native-issue-table-child-rows-query"
			fallback={
				<LoadingIssueRow
					itemId={parentItemId}
					groupId={groupId}
					columnsCount={columns.length}
					{...(isStickyColumnsVisible &&
						fg('issue_table_frozen_columns') && {
							stickyColumnConfigs: columns.map((column) => ({
								stickyOffsets: column.stickyOffsets,
								isColumnSticky: column.isColumnSticky,
							})),
						})}
					isFixedTableLayoutEnabled={props.isFixedTableLayoutEnabled}
					renderTableCell={props.renderTableCell}
					cellBorderStyle={props.cellBorderStyle}
				/>
			}
		>
			<ChildRowsPreloadedQuery
				issueSearch={issueSearch}
				depth={depth}
				parentItemId={parentItemId}
				itemsConnectionId={itemsConnectionId}
				parentIssueAri={parentIssueAri}
				parentItemConnectionId={parentItemConnectionId}
				parentIssueRelayConnectionId={parentIssueRelayConnectionId}
				parentIssueHierarchyLevel={parentIssueHierarchyLevel}
				connectionMetadata={connectionMetadata}
				isFormVisible={isFormVisible}
				formProps={formProps}
				projectKey={projectKey}
				isGroupingSupported={isGroupingSupported}
				childItemsExperience={childItemsExperience}
				childItemsLoadMoreExperience={childItemsLoadMoreExperience}
				analyticsSource={analyticsSource}
				pageSize={pageSize}
				width={width}
				isIssueRankDescending={isIssueRankDescending}
				renderRows={renderRows}
				renderCreateIssueController={renderCreateIssueController}
				groupId={groupId}
				columns={columns}
				rankFieldId={rankFieldId}
				renderDraggableWrapper={renderDraggableWrapper}
				ChildIssueCreateRowComponent={ChildIssueCreateRowComponent}
			/>
		</Placeholder>
	);
};

export type ChildRowsPreloadedQueryProps = ChildRowsPaginationBaseProps<Column> & {
	issueSearch: PreloadedQuery<ChildRowsPreloadedQueryType>;
};

export const ChildRowsPreloadedQuery = ({ issueSearch, ...rest }: ChildRowsPreloadedQueryProps) => {
	const queryData = usePreloadedQuery<ChildRowsPreloadedQueryType>(
		graphql`
			query ChildRowsPreloadedQuery(
				$cloudId: ID!
				$issueSearchInput: JiraIssueSearchInput!
				$first: Int
				$last: Int
				$before: String
				$after: String
				$options: JiraIssueSearchOptions
				$fieldSetsInput: JiraIssueSearchFieldSetsInput!
				$projectKeys: [String!]
				$projectKey: String
				$viewConfigInput: JiraIssueSearchViewConfigInput
				$shouldQueryHasChildren: Boolean!
				$isReparentingEnabled: Boolean!
				$isTimelineEnabled: Boolean!
				$scope: JiraIssueSearchScope
				$isConditionalFormattingEnabled: Boolean!
				$isLinkRelationshipFieldEnabled: Boolean!
			) {
				...ChildRowsPagination
					@arguments(
						projectKeys: $projectKeys
						projectKey: $projectKey
						shouldQueryHasChildren: $shouldQueryHasChildren
						isReparentingEnabled: $isReparentingEnabled
						isTimelineEnabled: $isTimelineEnabled
						isConditionalFormattingEnabled: $isConditionalFormattingEnabled
						isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
					)
				jira {
					issueSearch(
						cloudId: $cloudId
						issueSearchInput: $issueSearchInput
						first: $first
						last: $last
						before: $before
						after: $after
						options: $options
						fieldSetsInput: $fieldSetsInput
						viewConfigInput: $viewConfigInput
						scope: $scope
					) @optIn(to: "JiraSpreadsheetComponent-M1") {
						issueSearchError {
							__typename
							... on JiraServerError {
								message
							}
							... on JiraInvalidSyntaxError {
								message
							}
							... on JiraInvalidJqlError {
								messages
							}
						}
					}
				}
			}
		`,
		issueSearch,
	);

	if (rest.isGroupingSupported && queryData?.jira?.issueSearch?.issueSearchError?.__typename) {
		switch (queryData.jira.issueSearch.issueSearchError.__typename) {
			case 'JiraInvalidJqlError':
				throw new Error(queryData.jira.issueSearch.issueSearchError.messages?.[0] ?? 'Invalid JQL');
			case 'JiraInvalidSyntaxError':
			case 'JiraServerError':
				throw new Error(queryData.jira.issueSearch.issueSearchError.message ?? 'Unknown Error');
			default:
		}
	}

	return <ChildRowsPagination {...rest} query={queryData} />;
};
